// frontend/src/pages/ProfileGate/steps/ConsentNoticesStep.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import ProfileGateShell from "../../../components/profile-gate/ProfileGateShell";

import {
  awardBusinessProfileXpIfNeeded,
  markOnboardingCompleted,
} from "../../../lib/onboarding/profileGate";

import {
  clearProfileGateResumeStep,
} from "../../../lib/onboarding/profileGateResumeProgress";

import {
  completeMyConsentReview,
  getMyConsentGateStatus,
} from "../../../lib/legal/consent";

const CONSENT_RETURN_KEY =
  "gwin_consent_review_return_to";

const DOCUMENT_DESCRIPTIONS = {
  terms_of_use:
    "The rules for using Gwin, including account access, acceptable use, content restrictions and platform responsibilities.",

  privacy_policy:
    "How Gwin collects, uses, stores, protects, shares and deletes personal and business information.",

  cookie_policy:
    "How Gwin uses necessary and optional cookies or similar technologies.",

  popia_notice:
    "How Gwin processes personal, business, learning, progress and platform-activity information.",

  refund_policy:
    "How billing disputes, refunds, cancellations and payment-related matters are handled.",

  scoring_disclaimer:
    "The limits of Gwin scores, readiness indicators, educational information and funding-related guidance.",
};

function getSafeReturnTo(location) {
  const locationReturnTo =
    location.state?.returnTo;

  if (
    typeof locationReturnTo === "string" &&
    locationReturnTo.startsWith("/") &&
    !locationReturnTo.startsWith("/profile-gate")
  ) {
    return locationReturnTo;
  }

  const storedReturnTo =
    window.sessionStorage.getItem(
      CONSENT_RETURN_KEY
    );

  if (
    typeof storedReturnTo === "string" &&
    storedReturnTo.startsWith("/") &&
    !storedReturnTo.startsWith("/profile-gate")
  ) {
    return storedReturnTo;
  }

  return "/dashboard";
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date.toLocaleDateString(
    "en-ZA",
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );
}

export default function ConsentNoticesStep() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const finishOnboarding = useCallback(async () => {
    await markOnboardingCompleted();

    clearProfileGateResumeStep();

    window.sessionStorage.removeItem(CONSENT_RETURN_KEY);

    navigate("/dashboard", { replace: true });
  }, [navigate]);

  /*
   * RequireCurrentConsents sends existing users here with:
   *
   * consentReason: "updated"
   *
   * Anything else is treated as part of the Profile Gate /
   * onboarding flow.
   *
   * This is intentional:
   * an account redoing onboarding may already have historical
   * consent/onboarding records, so gateStatus.isInitialSetup
   * cannot reliably tell us whether this page is being used
   * as part of the current onboarding journey.
   */
  const isOnboardingFlow =
    location.state?.consentReason !==
    "updated";

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    gateStatus,
    setGateStatus,
  ] = useState(null);

  const [
    acknowledgedDocuments,
    setAcknowledgedDocuments,
  ] = useState({});

  const [
    optionalChoices,
    setOptionalChoices,
  ] = useState({});

  const returnTo =
    useMemo(
      () =>
        getSafeReturnTo(
          location
        ),
      [location]
    );

  useEffect(() => {
    if (
      location.state?.returnTo &&
      typeof location.state.returnTo ===
        "string"
    ) {
      window.sessionStorage.setItem(
        CONSENT_RETURN_KEY,
        location.state.returnTo
      );
    }
  }, [
    location.state,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadConsentReview() {
      try {
        setLoading(true);
        setError("");

        const status = await getMyConsentGateStatus();
        if (cancelled) return;

        if (!status.requiresReview) {
          if (isOnboardingFlow) {
            await awardBusinessProfileXpIfNeeded();
            if (cancelled) return;
            await finishOnboarding();
            return;
          }

          window.sessionStorage.removeItem(CONSENT_RETURN_KEY);
          navigate(returnTo, { replace: true });
          return;
        }

        setGateStatus(status);
        setAcknowledgedDocuments(
          Object.fromEntries(
            (status.pendingDocuments || []).map((document) => [
              document.document_id,
              false,
            ])
          )
        );
        setOptionalChoices(
          Object.fromEntries(
            (status.pendingPurposes || [])
              .filter((purpose) => purpose.permission_type === "optional")
              .map((purpose) => [purpose.purpose_key, false])
          )
        );
      } catch (loadError) {
        console.error("Failed to load consent review:", loadError);
        if (!cancelled) {
          setError(
            loadError?.message ||
              "We could not load your privacy choices. Please check your connection and try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadConsentReview();
    return () => {
      cancelled = true;
    };
  }, [navigate, returnTo, isOnboardingFlow, finishOnboarding]);

  const pendingDocuments =
    gateStatus
      ?.pendingDocuments ||
    [];

  const visiblePendingDocuments =
    pendingDocuments.filter(
      (document) =>
        document.document_key !==
        "scoring_disclaimer"
    );

  const allDocumentsAcknowledged =
    visiblePendingDocuments.every(
      (document) =>
        acknowledgedDocuments[
          document.document_id
        ] === true
    );

  const canSubmit =
    !loading &&
    !saving &&
    allDocumentsAcknowledged;

  function toggleDocument(
    documentId
  ) {
    setError("");

    setAcknowledgedDocuments(
      (current) => ({
        ...current,

        [documentId]:
          !current[
            documentId
          ],
      })
    );
  }

  function toggleSelectAllDocuments() {
    setError("");

    const nextValue =
      !allDocumentsAcknowledged;

    setAcknowledgedDocuments(
      (current) => ({
        ...current,

        ...Object.fromEntries(
          visiblePendingDocuments.map(
            (document) => [
              document.document_id,
              nextValue,
            ]
          )
        ),
      })
    );
  }

  async function handleNext() {
  if (!canSubmit) {
    return;
  }

  try {
    setSaving(true);
    setError("");

    await completeMyConsentReview({
      optionalChoices,
      source: isOnboardingFlow
        ? "onboarding"
        : "updated_consent_prompt",
    });

    if (isOnboardingFlow) {
      await awardBusinessProfileXpIfNeeded();
      await finishOnboarding();
      return;
    }

    // Returning existing users after updated consent.
    window.sessionStorage.removeItem(
      CONSENT_RETURN_KEY
    );

    navigate(returnTo, {
      replace: true,
    });
  } catch (saveError) {
    console.error(
      "Failed to save consent review:",
      saveError
    );

    setError(
      saveError?.message ||
        "We could not save your privacy choices. Please try again."
    );
  } finally {
    setSaving(false);
  }
}

  function handleBack() {
    if (
      isOnboardingFlow
    ) {
      navigate(
        "/profile-gate/onboarding-complete"
      );

      return;
    }

    /*
     * An updated mandatory consent review
     * cannot be bypassed.
     */
    navigate(
      "/logout",
      {
        replace: true,
      }
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFF7ED] px-6">
        <p className="text-sm font-semibold text-[#757575]">
          Loading your privacy choices...
        </p>
      </div>
    );
  }

  return (
    <ProfileGateShell
      title=""
      subtitle=""
      progress={
        isOnboardingFlow
          ? "Legal notices"
          : "Updated privacy review"
      }
      progressPercent={
        isOnboardingFlow
          ? 95
          : 100
      }
      onBack={
        handleBack
      }
      onNext={
        handleNext
      }
      nextLabel={
        saving
          ? "SAVING..."
          : isOnboardingFlow
          ? "CONTINUE"
          : "SAVE CHOICES"
      }
      nextDisabled={
        !canSubmit
      }
    >
      <div className="mx-auto w-full max-w-[720px]">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#FF914D]">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#FFF4EC] text-[17px]">
            📄
          </span>

          Legal notices
        </div>

        <h1 className="mt-3 text-[30px] font-bold leading-tight tracking-[-0.03em] text-[#333333] sm:text-[36px]">
          {isOnboardingFlow
            ? "Review your legal notices"
            : "Your privacy notices have been updated"}
        </h1>

        <p className="mt-3 max-w-[640px] text-[15px] leading-relaxed text-[#757575] sm:text-[16px]">
          {isOnboardingFlow
            ? "Please review and acknowledge the required legal documents before continuing."
            : "One or more legal documents or data-processing permissions have changed. Please review the updated information and confirm your choices again."}
        </p>

        {!isOnboardingFlow && (
          <div className="mt-6 rounded-2xl border border-[#FFD3B8] bg-[#FFF4EC] px-4 py-4">
            <p className="text-sm font-bold text-[#333333]">
              Why you are seeing this
            </p>

            <p className="mt-1 text-[13px] leading-relaxed text-[#757575]">
              Gwin detected a new document,
              changed wording or a new consent
              revision. Your previous decision
              does not automatically apply to the
              updated version.
            </p>
          </div>
        )}

        {visiblePendingDocuments.length >
          0 && (
          <section className="mt-8 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-base font-bold text-[#333333]">
                  Legal documents
                </p>

                <p className="mt-1 text-[13px] leading-relaxed text-[#757575]">
                  Open any document you want to read in full, or use Select all to acknowledge the required documents together.
                </p>
              </div>

              <button
                type="button"
                onClick={
                  toggleSelectAllDocuments
                }
                className="shrink-0 rounded-xl border border-[#FF914D] bg-white px-4 py-2 text-sm font-extrabold text-[#D85F20] transition hover:bg-[#FFF4EC]"
              >
                {allDocumentsAcknowledged
                  ? "Clear all"
                  : "Select all"}
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {visiblePendingDocuments.map(
                (
                  document
                ) => {
                  const checked =
                    acknowledgedDocuments[
                      document
                        .document_id
                    ] === true;

                  const description =
                    DOCUMENT_DESCRIPTIONS[
                      document
                        .document_key
                    ] ||
                    "Review this legal document before continuing.";

                  return (
                    <label
                      key={
                        document.document_id
                      }
                      className={[
                        "flex cursor-pointer gap-3 rounded-2xl border p-4 transition-all",

                        checked
                          ? "border-[#FF914D] bg-[#FFF4EC]"
                          : "border-[#E5E5E5] bg-white hover:border-[#FF914D]/50",
                      ].join(
                        " "
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={
                          checked
                        }
                        onChange={() =>
                          toggleDocument(
                            document.document_id
                          )
                        }
                        className="mt-1 h-5 w-5 rounded border-[#D4D4D4] text-[#FF914D] focus:ring-[#FF914D]"
                      />

                      <span className="block min-w-0">
                        <span className="block text-sm font-bold text-[#333333]">
                          I have reviewed the{" "}

                          <Link
                            to={
                              document.route_path
                            }
                            state={{
                              backTo:
                                "/profile-gate/consent-notices",

                              backLabel:
                                "Back to consent",

                              replaceBack:
                                true,
                            }}
                            className="text-[#FF914D] underline hover:text-[#E9782F]"
                            onClick={(
                              event
                            ) =>
                              event.stopPropagation()
                            }
                          >
                            {
                              document.title
                            }
                          </Link>
                        </span>

                        <span className="mt-2 block rounded-xl bg-[#FAFAFA] px-3 py-2 text-[13px] leading-relaxed text-[#757575]">
                          <strong className="text-[#555555]">
                            Summary:{" "}
                          </strong>

                          {
                            description
                          }
                        </span>

                        <span className="mt-2 block text-[11px] font-semibold text-[#999999]">
                          Version{" "}

                          {document.version ||
                            `revision ${document.revision}`}

                          {document.effective_at
                            ? ` • Effective ${formatDate(
                                document.effective_at
                              )}`
                            : ""}
                        </span>

                        {document.change_summary && (
                          <span className="mt-2 block rounded-xl bg-white/80 px-3 py-2 text-[12px] leading-relaxed text-[#555555]">
                            <strong>
                              What changed:
                            </strong>{" "}

                            {
                              document.change_summary
                            }
                          </span>
                        )}
                      </span>
                    </label>
                  );
                }
              )}
            </div>
          </section>
        )}

        {error && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-[13px] font-semibold leading-snug text-red-700">
              {error}
            </p>
          </div>
        )}
      </div>
    </ProfileGateShell>
  );
}